import axios from 'axios';

// API Configuration - Updated: Fix redirect to root
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.pujapathapp.com';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors (disabled for hardcoded auth)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Removed automatic redirect on 401 - using hardcoded credentials
    // Just pass through the error without clearing auth state
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) =>
    apiClient.post('/api/user/login/', credentials),

  signup: (userData) =>
    apiClient.post('/api/user/signup/', userData),

  refreshToken: (refreshToken) =>
    apiClient.post('/api/user/refresh/', { refresh: refreshToken }),
};

// Horoscope API
export const horoscopeAPI = {
  // Get all horoscopes with optional filtering
  getAll: (params = {}) =>
    apiClient.get('/api/rashi/horoscope/', { params }),

  // Get daily horoscopes (today, tomorrow, weekly)
  getDailyHoroscopes: () =>
    apiClient.get('/api/rashi/horoscope/', { params: { collection: 'daily' } }),

  // Get period horoscopes (monthly, yearly)
  getPeriodHoroscopes: () =>
    apiClient.get('/api/rashi/horoscope/', { params: { collection: 'period' } }),

  // Get horoscope by sign and timespan
  getHoroscope: (birthdate, timespan) =>
    apiClient.post('/api/rashi/horoscope/', { birthdate, timespan }),

  // Create new horoscope
  create: (horoscopeData) =>
    apiClient.post('/api/rashi/horoscope/create/', horoscopeData),

  // Delete horoscope
  delete: (id) =>
    apiClient.delete(`/api/rashi/horoscope/${id}/`),

  // Delete all horoscopes
  deleteAll: (collectionType = 'all') =>
    apiClient.delete('/api/rashi/horoscope/delete-all/', {
      params: { collection: collectionType }
    }),

  // Get horoscopes by timespan
  getByTimespan: (timespan) =>
    apiClient.get('/api/rashi/horoscope/', { params: { timespan } }),
};

// System API
export const systemAPI = {
  // Check API status
  getStatus: () =>
    apiClient.get('/api/status/'),

  // Check admin status
  getAdminStatus: () =>
    apiClient.get('/api/admin-status/'),

  // Get MongoDB stats
  getMongoStats: () =>
    apiClient.get('/admin/mongo-stats/'),
};

// MongoDB Direct API (for advanced operations)
export const mongoAPI = {
  // Get all collections
  getCollections: () =>
    apiClient.get('/api/mongo/collections/'),

  // Get collection data
  getCollectionData: (collectionName) =>
    apiClient.get(`/api/mongo/collection/${collectionName}/`),

  // Insert document
  insertDocument: (collectionName, document) =>
    apiClient.post(`/api/mongo/collection/${collectionName}/`, document),

  // Update document
  updateDocument: (collectionName, documentId, document) =>
    apiClient.put(`/api/mongo/collection/${collectionName}/${documentId}/`, document),

  // Delete document
  deleteDocument: (collectionName, documentId) =>
    apiClient.delete(`/api/mongo/collection/${collectionName}/${documentId}/`),
};

// Pandit Management API
export const panditAPI = {
  // Get all pandits (no auth required)
  getAll: () =>
    apiClient.get('/api/pandit/pandits/'),

  // Create new pandit (requires auth)
  create: (panditData) =>
    apiClient.post('/api/pandit/pandits/', panditData),

  // Get pandits by location (requires auth)
  getByLocation: (location) =>
    apiClient.get(`/api/pandit/location/${location}/`),

  // Delete pandit (requires auth)
  delete: (panditName, location) =>
    apiClient.delete('/api/pandit/delete/', {
      data: { Pandit_name: panditName, Location: location }
    }),

  // Check database status
  getStatus: () =>
    apiClient.get('/api/pandit/status/'),
};

// Zodiac signs configuration
export const ZODIAC_SIGNS = [
  { value: 'aries', label: 'Aries ♈', emoji: '♈', dates: 'Mar 21 - Apr 19' },
  { value: 'taurus', label: 'Taurus ♉', emoji: '♉', dates: 'Apr 20 - May 20' },
  { value: 'gemini', label: 'Gemini ♊', emoji: '♊', dates: 'May 21 - Jun 20' },
  { value: 'cancer', label: 'Cancer ♋', emoji: '♋', dates: 'Jun 21 - Jul 22' },
  { value: 'leo', label: 'Leo ♌', emoji: '♌', dates: 'Jul 23 - Aug 22' },
  { value: 'virgo', label: 'Virgo ♝', emoji: '♝', dates: 'Aug 23 - Sep 22' },
  { value: 'libra', label: 'Libra ♎', emoji: '♎', dates: 'Sep 23 - Oct 22' },
  { value: 'scorpio', label: 'Scorpio ♝', emoji: '♝', dates: 'Oct 23 - Nov 21' },
  { value: 'sagittarius', label: 'Sagittarius ♝', emoji: '♝', dates: 'Nov 22 - Dec 21' },
  { value: 'capricorn', label: 'Capricorn ♑', emoji: '♑', dates: 'Dec 22 - Jan 19' },
  { value: 'aquarius', label: 'Aquarius ♒', emoji: '♒', dates: 'Jan 20 - Feb 18' },
  { value: 'pisces', label: 'Pisces ♓', emoji: '♓', dates: 'Feb 19 - Mar 20' },
];

// Timespan options
export const TIMESPAN_OPTIONS = [
  { value: 'today', label: 'Today', icon: '📅' },
  { value: 'tomorrow', label: 'Tomorrow', icon: '🔮' },
  { value: 'weekly', label: 'Weekly', icon: '📆' },
  { value: 'monthly', label: 'Monthly', icon: '🗓︝' },
  { value: 'yearly', label: 'Yearly', icon: '📊' },
];

export default apiClient;
